import React, { useState, useRef, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateUserDetails, updateProfile } from "../../redux/auth/actions";
import {
  previewPrimaryColor,
  updateCompany,
  updateLogo,
} from "../../redux/company/actions";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import chroma from "chroma-js";

import { HexColorPicker } from "react-colorful";
import toast from "react-hot-toast";
import SoftDrawer from "../SoftDrawer";
import { GlowEffect } from "../../styles/effects";

import { UseIsAdmin, UseUserDetails, UseIsDemo, UseIsPrime } from "../../hooks/auth";
import {
  UseCompanyLogo,
  UseCompanyName,
  UseCompanyColors,
  UseCompany,
} from "../../hooks/company";
import { UPDATE_USER_LOGO, UPDATE_USER_IMAGE } from "../../api/user";
import { UPLOAD_LOGO } from "../../api/company";
import { GET_IMAGE } from "../../helpers/images";
import onboarding from "../../constants/onboarding";
import { BRIGHTNESS_MIN, BRIGHTNESS_MAX } from "../../theme";
import AdminViewToggle from "./AdminViewToggle";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import "./customizationSettings.css";

// Static avatar thumbnail: the drawer used to mount a live WebGL <Avatar>
// canvas here, which frequently rendered blank (canvas sizing inside the
// sliding drawer). ReadyPlayerMe serves a 2D portrait render of any .glb at
// the same URL with a .png extension — instant and always paints.
const avatarPngOf = (glbUrl) => {
  if (!glbUrl || typeof glbUrl !== "string") return null;
  if (!/readyplayer\.me\/.*\.glb/i.test(glbUrl)) return null;
  return glbUrl.replace(/\.glb(\?.*)?$/i, ".png");
};

/* Small inline line icons — same Lucide style the rest of the app uses. */
const Ic = ({ path, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    {path}
  </svg>
);
const xPath = <path d="M18 6 6 18M6 6l12 12" />;
const arrowPath = <path d="M5 12h14M13 6l6 6-6 6" />;
const resetPath = <><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></>;

const CustomizationSettings = ({ open, handleClose }) => {
  const [openColors, setOpenColors] = useState(open && false);
  const [resolveSettings, setResolveSettings] = useState(false);
  const {
    _id: userId,
    image,
    firstName,
    avatar,
    onboardingStatus: userStatus,
  } = UseUserDetails();
  const isDemo = UseIsDemo();
  const { isAdmin } = UseUserDetails();
  const isPrime = UseIsPrime();

  const { _id: companyId, onboardingStatus: companyStatus } = UseCompany();
  const status = isDemo ? userStatus : companyStatus;
  const customized = useMemo(
    () =>
      // Flag that determines if the user has customized or attempted to customize their profile at this time
      resolveSettings ||
      !(isAdmin || isDemo) ||
      (status &&
        status !== onboarding.ONGOING &&
        status !== onboarding.TRIAL_SIGNUP),
    [isAdmin, status, resolveSettings, userId],
  );
  const logo = UseCompanyLogo();
  const companyName = UseCompanyName();
  const { primaryColor: companyColor } = UseCompanyColors();
  const [primary, setPrimary] = useState(companyColor);
  const [convertedColor, setConvertedColor] = useState(companyColor);

  const logoInputRef = useRef();
  const profileInputRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Swatch preview — the live primary plus two lighter steps of it.
  const swatches = useMemo(() => {
    try {
      const base = primary || companyColor || "#1b598e";
      return [base, chroma(base).brighten(0.9).hex(), chroma(base).brighten(1.8).hex()];
    } catch (e) {
      return ["#1b598e", "#2e7bc4", "#85b7eb"];
    }
  }, [primary, companyColor]);

  const setCompanyColor = debounce((color) => {
    let recolor = color;
    const hsv = chroma(color).hsv();
    if (hsv[2] > BRIGHTNESS_MAX) {
      recolor = chroma.hsv(hsv[0], hsv[1], BRIGHTNESS_MAX).hex();
    } else if (hsv[2] < BRIGHTNESS_MIN) {
      recolor = chroma.hsv(hsv[0], hsv[1], BRIGHTNESS_MIN).hex();
    }
    setPrimary(color);
    setConvertedColor(recolor);
    if (isDemo) {
      dispatch(updateUserDetails({ primaryColor: recolor }));
    } else {
      dispatch(previewPrimaryColor(recolor));
    }
  }, 350);

  const resetColor = () => {
    setPrimary("#EE238A");
    setConvertedColor("#EE238A");
    if (isDemo) {
      dispatch(updateUserDetails({ primaryColor: "#EE238A" }));
    } else {
      dispatch(previewPrimaryColor("#EE238A"));
    }
  };

  const handleUpload = async (type, e) => {
    try {
      if (e.target.files.length === 0) {
        return;
      }
      const imgFile = e.target.files[0];
      const formData = new FormData();
      formData.append("image", imgFile);

      const upload = toast.loading("Uploading...");

      if (type === "profile") {
        const { data } = await UPDATE_USER_IMAGE(userId, formData);
        dispatch(updateUserDetails({ image: data }));

        toast.success("Profile photo updated", {
          id: upload,
        });
      } else {
        setOpenColors(false);
        if (isDemo) {
          const {
            data: { companyLogo, primaryColor },
          } = await UPDATE_USER_LOGO(formData);
          setCompanyColor(primaryColor);
          dispatch(updateUserDetails({ companyLogo, primaryColor }));
        } else {
          const {
            data: { companyLogo, primaryColor },
          } = await UPLOAD_LOGO(companyId, formData);
          setCompanyColor(primaryColor);
          dispatch(updateLogo(companyLogo));
        }

        toast.success("Logo updated", {
          id: upload,
        });
      }
    } catch (error) {
      toast.dismiss();
      toast.error("Upload failed");
    }
  };

  const handleSave = () => {
    if (isDemo) {
      dispatch(
        // Demo users customize logo and colors for their profile only
        updateProfile(userId, {
          companyLogo: logo,
          primaryColor: convertedColor,
          ...(!!logo &&
            !!convertedColor && { onboardingStatus: onboarding.CUSTOMIZED }),
        }),
      );
    } else {
      // Regular admin users customize company-level logo and colors
      dispatch(
        updateCompany({
          logo,
          primaryColor: convertedColor,
          ...(!!convertedColor && { onboardingStatus: onboarding.CUSTOMIZED }),
        }),
      );
    }
    setResolveSettings(true);
    handleClose();
  };

  // Onboarding nudge — glow the action that still needs doing.
  const glowUp = (children, glow) =>
    glow ? <GlowEffect borderRadius="lg">{children}</GlowEffect> : children;

  const drawerOpen = open || (!!companyId && !customized);
  const avatarThumb = avatarPngOf(avatar);

  return (
    <SoftDrawer
      variant="temporary"
      shadow="xxl"
      open={drawerOpen} // check customized only after the company info has been loaded
      onClose={handleSave}
      anchor="right"
      PaperProps={{ className: "no-scrollbar cz-paper" }}
      ModalProps={{ BackdropProps: { className: "cz-backdrop" } }}
    >
      <div className="cz-root">
        <div className="cz-head">
          <img className="cz-head-ava" src={GET_IMAGE(image)} alt={firstName || "Player"} />
          <div className="cz-head-who">
            <div className="cz-title">{firstName || "Player"}</div>
            <div className="cz-sub">
              {isAdmin ? "Admin" : "Player"}
              {companyName ? ` · ${companyName}` : ""}
            </div>
          </div>
          <button type="button" className="cz-x" onClick={handleSave} aria-label="Close">
            <Ic path={xPath} size={17} />
          </button>
        </div>

        {(isAdmin || isDemo) && (
          <div>
            <div className="cz-sec">Your company</div>
            <div className="cz-group">
            <div className="cz-row">
              <img className="cz-thumb" src={GET_IMAGE(logo)} alt={companyName || "Company logo"} />
              <span className="cz-lab">
                <span className="t">Company logo</span>
                <span className="d">Shown across the whole game</span>
              </span>
              {glowUp(
                <button type="button" className="cz-btn" onClick={() => logoInputRef.current.click()}>
                  Change
                </button>,
                !customized && !logo,
              )}
              <input
                type="file"
                accept="image/png, image/jpeg"
                ref={logoInputRef}
                onChange={(e) => handleUpload("logo", e)}
                hidden
              />
            </div>
            <div className="cz-row">
              <span className="cz-swatches">
                {swatches.map((c) => (
                  <span key={c} className="cz-swatch" style={{ background: c }} />
                ))}
              </span>
              <span className="cz-lab">
                <span className="t">Theme colors</span>
                <span className="d">Tints the whole app</span>
              </span>
              {glowUp(
                <button
                  type="button"
                  className="cz-btn"
                  onClick={() => setOpenColors((prevVal) => !prevVal)}
                >
                  {openColors ? "Done" : "Edit"}
                </button>,
                !customized && !companyColor && !openColors,
              )}
            </div>
            {openColors && (
              <div className="cz-picker">
                <HexColorPicker
                  color={primary}
                  onChange={(e) => setCompanyColor(e)}
                  style={{ width: "100%", height: "150px" }}
                />
                <button type="button" className="cz-reset" onClick={resetColor}>
                  <Ic path={resetPath} size={13} />
                  Reset to the default theme
                </button>
              </div>
            )}
            </div>
          </div>
        )}

        <div>
        <div className="cz-sec">Your profile</div>
        <div className="cz-group">
        <div className="cz-row">
          <img className="cz-thumb round" src={GET_IMAGE(image)} alt={firstName || "Player"} />
          <span className="cz-lab">
            <span className="t">Profile photo</span>
          </span>
          <button type="button" className="cz-btn" onClick={() => profileInputRef.current.click()}>
            Change
          </button>
          <input
            type="file"
            accept="image/png, image/jpeg"
            ref={profileInputRef}
            onChange={(e) => handleUpload("profile", e)}
            hidden
          />
        </div>
        <div className="cz-row">
          <span className="cz-ava3d" style={{ position: "relative" }}>
            {/* Silhouette base layer — shows when there's no RPM avatar OR the
                portrait render 404s (stale avatar ids do). Never a broken-image icon. */}
            <span
              aria-hidden="true"
              style={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
            >
              <svg width="24" height="30" viewBox="0 0 42 52" fill="none">
                <circle cx="21" cy="16" r="10" fill="#B9C4D2" />
                <path d="M3 52c0-10.5 8.1-18 18-18s18 7.5 18 18" fill="#B9C4D2" />
              </svg>
            </span>
            {avatarThumb && (
              <img
                src={avatarThumb}
                alt=""
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                style={{ position: "relative", width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            )}
          </span>
          <span className="cz-lab">
            <span className="t">3D avatar</span>
            <span className="d">Your character on the gameboard</span>
          </span>
          <button
            type="button"
            className="cz-btn"
            onClick={() => {
              handleSave();
              navigate("/avatar");
            }}
          >
            Edit
          </button>
        </div>
        </div>
        </div>

        {isAdmin && (
        <div>
        <div className="cz-sec">View</div>
        <div className="cz-group">
          <div className="cz-row stack">
            <span className="cz-lab">
              <span className="t">See the app as</span>
            </span>
            <span className="cz-toggle-track">
              <AdminViewToggle />
            </span>
          </div>
        {isPrime && (
          <div
            className="cz-row nothumb link"
            role="button"
            tabIndex={0}
            onClick={() => {
              // Used window.location.replace instead of react-router-dom navigate to fully refresh the page for TV
              window.location.replace(`${window.location.origin}/live/leaderboard/tv`);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                window.location.replace(`${window.location.origin}/live/leaderboard/tv`);
              }
            }}
          >
            <span className="cz-lab">
              <span className="t" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <LiveTvIcon sx={{ fontSize: "16px" }} />
                Leaderboard TV view
              </span>
              <span className="d">Full screen for the office TV</span>
            </span>
            <span className="cz-arrow">
              <Ic path={arrowPath} size={15} />
            </span>
          </div>
        )}
        </div>
        </div>
        )}

        <div className="cz-foot">
          <button type="button" className="cz-logout" onClick={() => navigate("/signout")}>
            Log out
          </button>
        </div>
      </div>
    </SoftDrawer>
  );
};

export default CustomizationSettings;
